import { useEffect, useState } from "react";
import { Info } from "lucide-react";

/**
 * Small helper line shown under the "Continue with Google" button on
 * Login/Signup. iOS Safari aggressively blocks third-party state cookies
 * during the OAuth handshake, which causes the "Invalid state parameter"
 * error on the Emergent platform's auth gateway. We can't prevent that
 * from our app code, but we can warn users *before* they click and give
 * them an obvious fallback.
 */
export default function GoogleAuthHelper() {
  const [isIosSafari, setIsIosSafari] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // Real Safari (not Chrome/Edge/Firefox on iOS, which all share the WebKit base
    // but have their own UA strings — and importantly, they share the same ITP behavior anyway)
    const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    setIsIosSafari(iOS && webkit);
  }, []);

  if (!isIosSafari) {
    // Subtle universal helper — only one line
    return (
      <p className="text-xs text-[#7A736E] -mt-2 mb-4 text-center" data-testid="google-auth-helper">
        Trouble signing in with Google? <span className="text-[#3A5A4A]">Use email below instead.</span>
      </p>
    );
  }

  // iOS Safari — specific heads-up
  return (
    <div
      className="-mt-2 mb-4 p-3 rounded-xl bg-[#FBEFE9] border border-[#C87B67]/30 flex items-start gap-2.5"
      data-testid="ios-safari-google-notice"
    >
      <Info className="w-4 h-4 text-[#C87B67] flex-shrink-0 mt-0.5" />
      <p className="text-xs text-[#3D3935] leading-relaxed">
        <strong>iPhone tip:</strong> Safari sometimes blocks Google sign-in.
        If you see an &quot;invalid state&quot; error, use email below (it works every time)
        or open Hope Chest in Chrome.
      </p>
    </div>
  );
}
